import type { RouteRecordRaw } from 'vue-router';

import { wrapRuntimeImport } from '@/shared/helpers/async.helpers';
import { makePermissionMeta } from '@/shared/helpers/permissions.helpers';

// Every record carries its own meta because route guards evaluate each matched record's own meta
// (see routesWithAuthorization in permissions.helpers). Redirect-only records inherit their
// destination's authorization.
//
// Gated on the hosting permission alone — deliberately NOT on tenant-admin. A non-admin holding
// `hosting.sitesRead` is meant to reach these screens.
//
// Narrowed off `RouteMeta` on purpose: `RouteMeta` extends `Record<string, unknown>`, and that index
// signature stops TS discriminating the `RouteRecordRaw` union, which silently drops the contextual
// type on child `redirect: to => …` params (TS7006). The makePermissionMeta calls still type-check
// the app/feature names.
const sites = makePermissionMeta('hosting', 'sitesRead') as { app: string; feature: string };
const analytics = makePermissionMeta('hosting', 'analyticsRead') as { app: string; feature: string };

export const HostingRoutes: RouteRecordRaw[] = [
    { path: '', redirect: '/hosting/sites' },
    {
        path: 'sites',
        component: wrapRuntimeImport(() => import('./screens/sites.vue')),
        meta: sites
    },
    {
        path: 'sites/:siteId',
        component: wrapRuntimeImport(() => import('./screens/site/site.vue')),
        meta: sites,
        children: [
            { path: '', redirect: to => ({ path: `/hosting/sites/${to.params.siteId}/overview` }) },
            { path: 'overview', component: wrapRuntimeImport(() => import('./screens/site/overview.vue')), meta: sites },
            { path: 'domains', component: wrapRuntimeImport(() => import('./screens/site/domains.vue')), meta: sites },
            { path: 'deploy', component: wrapRuntimeImport(() => import('./screens/site/deploy.vue')), meta: sites },
            // Pre-rename path — keep old bookmarks/links working.
            { path: 'wordpress-deploy', redirect: to => ({ path: `/hosting/sites/${to.params.siteId}/deploy` }) },
            { path: 'databases', component: wrapRuntimeImport(() => import('./screens/site/databases.vue')), meta: sites },
            // Gated on sitesRead like every other site tab: the finer hosting.files* permissions are
            // enforced per-request by the API (tenant admins bypass them), and the screen hides the
            // actions it can't perform. Gating the route on filesRead would lock out an admin who
            // doesn't carry the flag explicitly.
            { path: 'files', component: wrapRuntimeImport(() => import('./screens/site/files.vue')), meta: sites },
            { path: 'sftp', component: wrapRuntimeImport(() => import('./screens/site/sftp.vue')), meta: sites },
            { path: 'cache', component: wrapRuntimeImport(() => import('./screens/site/cache.vue')), meta: sites },
            { path: 'redirects', component: wrapRuntimeImport(() => import('./screens/site/redirects.vue')), meta: sites },
            { path: 'security', component: wrapRuntimeImport(() => import('./screens/site/security.vue')), meta: sites },
            { path: 'analytics', component: wrapRuntimeImport(() => import('./screens/site/analytics.vue')), meta: analytics },
            { path: 'settings', component: wrapRuntimeImport(() => import('./screens/site/settings.vue')), meta: sites }
        ]
    },
    {
        // Standalone (NOT under the site wrapper) so it keeps polling after the site 404s out of the
        // source tenant on ownership commit. Sysadmin-only in practice: the action is `isSysAdmin`-gated
        // and the backend 403s non-sysadmins (the route uses `meta.admin` because `meta.sysadmin`
        // requires the ZC tenant, which is false while a sysadmin operates inside a customer tenant).
        // Deliberately admin-only, with no hosting permission: a sysadmin must still reach this while
        // operating inside a tenant whose hosting permissions are revoked.
        path: 'sites/:siteId/transfer',
        component: wrapRuntimeImport(() => import('./screens/site-transfer.vue')),
        meta: { admin: true, subAppName: 'Site Transfer' }
    },
    {
        path: 'domains',
        component: wrapRuntimeImport(() => import('./screens/domains.vue')),
        meta: { ...sites, subAppName: 'Domains' }
    },
    // Static segments must precede `domains/:domainId` so they aren't swallowed as domain IDs.
    {
        path: 'domains/connections',
        component: wrapRuntimeImport(() => import('./screens/domains/connections.vue')),
        meta: { ...sites, subAppName: 'Integrations' }
    },
    {
        path: 'domains/connections/new',
        component: wrapRuntimeImport(() => import('./screens/domains/connection-new.vue')),
        meta: { ...sites, subAppName: 'Connect a provider' }
    },
    {
        path: 'domains/import/:connectionId',
        component: wrapRuntimeImport(() => import('./screens/domains/import.vue')),
        meta: { ...sites, subAppName: 'Import' }
    },
    {
        path: 'domains/:domainId',
        component: wrapRuntimeImport(() => import('./screens/domains/domain.vue')),
        meta: sites,
        children: [
            { path: '', redirect: to => ({ path: `/hosting/domains/${to.params.domainId}/overview` }) },
            { path: 'overview', component: wrapRuntimeImport(() => import('./screens/domains/domain/overview.vue')), meta: sites },
            { path: 'dns', component: wrapRuntimeImport(() => import('./screens/domains/domain/dns.vue')), meta: sites }
        ]
    },
    {
        path: 'analytics',
        component: wrapRuntimeImport(() => import('./screens/analytics/properties.vue')),
        meta: { ...analytics, subAppName: 'Analytics' }
    },
    {
        path: 'analytics/:propertyId',
        component: wrapRuntimeImport(() => import('./screens/analytics/property.vue')),
        meta: analytics,
        children: [
            { path: '', redirect: to => ({ path: `/hosting/analytics/${to.params.propertyId}/overview` }) },
            { path: 'overview', component: wrapRuntimeImport(() => import('./screens/analytics/property/overview.vue')), meta: analytics },
            { path: 'paths', component: wrapRuntimeImport(() => import('./screens/analytics/property/paths.vue')), meta: analytics },
            { path: 'audience', component: wrapRuntimeImport(() => import('./screens/analytics/property/audience.vue')), meta: analytics },
            { path: 'journeys', component: wrapRuntimeImport(() => import('./screens/analytics/property/journeys.vue')), meta: analytics }
        ]
    },
    {
        path: 'monitoring',
        component: wrapRuntimeImport(() => import('./screens/monitoring.vue')),
        meta: { ...sites, subAppName: 'Monitoring' }
    }
];
